import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
} from "@mui/material";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

// import { motion } from "framer-motion";
import { keyframes } from "@mui/system";

import api from "../api";
import Loader from "../components/common/loader";

/* -------------------- CONSTANTS -------------------- */
const fyersFields = [
  "FYERS_APP_ID",
  "FYERS_SECRET",
  "FYERS_REDIRECT_URL",
  "AUTH_CODE_GEN_URL",
];

const requiredFields = ["FYERS_APP_ID", "FYERS_SECRET", "FYERS_REDIRECT_URL"];

const normalizeData = (data) => {
  const normalized = {};
  fyersFields.forEach((key) => {
    normalized[key] = data[key] ?? "";
  });
  return normalized;
};

/* -------------------- ANIMATION -------------------- */
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

/* -------------------- STYLES -------------------- */
const glassCard = {
  borderRadius: 4,
  background: "rgba(255,255,255,0.6)",
  backdropFilter: "blur(18px)",
  border: "1px solid rgba(255,255,255,0.35)",
  boxShadow: "0 10px 40px rgba(0,0,0,0.12)",
};

const glassInput = {
  background: "rgba(255,255,255,0.65)",
  backdropFilter: "blur(12px)",
  borderRadius: 2,
  "& .MuiOutlinedInput-root": {
    "& fieldset": {
      borderColor: "rgba(0,0,0,0.08)",
    },
    "&:hover fieldset": {
      borderColor: "rgba(25,118,210,0.3)",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#1976d2",
    },
  },
};

const glassButton = {
  textTransform: "none",
  fontWeight: 600,
  borderRadius: 999,
  px: 3,
  py: 1.2,

  background:
    "linear-gradient(135deg, rgba(255,255,255,0.7), rgba(255,255,255,0.4))",

  backdropFilter: "blur(12px)",

  border: "1px solid rgba(255,255,255,0.5)",

  color: "#073057",

  boxShadow: "0 4px 14px rgba(0,0,0,0.08), inset 0 1px 0 rgba(255,255,255,0.6)",

  transition: "all 0.25s cubic-bezier(0.22,1,0.36,1)",

  "&:hover": {
    transform: "translateY(-3px) scale(1.03)",
    boxShadow:
      "0 10px 30px rgba(0,0,0,0.15), inset 0 1px 0 rgba(255,255,255,0.8)",
    background:
      "linear-gradient(135deg, rgba(0, 200, 255, 0.5), rgba(255,255,255,0.5))",
  },

  "&:active": {
    transform: "scale(0.98)",
  },
};

/* -------------------- COMPONENT -------------------- */
const AuthCodeManager = () => {
  const [authCode, setAuthCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [token, setToken] = useState("");
  const [fyersData, setFyersData] = useState(null);
  const [originalData, setOriginalData] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showConsentPrompt, setShowConsentPrompt] = useState(false);

  /* ---------------- FETCH ---------------- */
  useEffect(() => {
    const fetchFyers = async () => {
      try {
        const res = await api.get("/fyers");

        if (res.status === 200 && res.data) {
          const normalized = normalizeData(res.data);
          setFyersData(normalized);
          setOriginalData(normalized);
          setShowForm(true);
        } else {
          const normalized = normalizeData({});
          setFyersData(normalized);
          setOriginalData(normalized);
          setShowConsentPrompt(true);
        }
      } catch {
        const normalized = normalizeData({});
        setFyersData(normalized);
        setOriginalData(normalized);
        setShowConsentPrompt(true);
      }
    };

    fetchFyers();
  }, []);

  /* ---------------- TOKEN ---------------- */
  const handleGenerateAccessToken = async () => {
    try {
      setLoading(true);
      setMessage("");
      setToken("");

      const params = {};
      if (authCode) params.auth_code = authCode;

      const response = await api.get("/gen-access-token", { params });

      setMessage(response.data.message || "Token generated");
      if (response.data.token) setToken(response.data.token);
    } catch (err) {
      setMessage(err.response?.data?.message || "Error generating token");
    } finally {
      setLoading(false);
    }
  };

  /* ---------------- SAVE ---------------- */
  const handleSaveChanges = async () => {
    if (!window.confirm("Save changes?")) return;

    try {
      setSaving(true);
      const res = await api.put("/fyers", fyersData);
      setMessage(res.data.message || "Saved!");
      setOriginalData(fyersData);
    } catch {
      setMessage("Failed to update.");
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (key, value) => {
    setFyersData({ ...fyersData, [key]: value });
  };

  const handleCopy = (value) => {
    navigator.clipboard.writeText(value);
  };

  if (!fyersData) return <Loader loading={true} />;

  /* ---------------- CONSENT ---------------- */
  if (showConsentPrompt && !showForm) {
    return (
      <Box sx={{ textAlign: "center", mt: 6 }}>
        <Typography sx={{ mb: 2 }}>
          Add your Fyers credentials to continue.
        </Typography>

        <Button
          sx={glassButton}
          onClick={() => {
            setShowConsentPrompt(false);
            setShowForm(true);
          }}
        >
          Add Credentials
        </Button>
      </Box>
    );
  }

  const changesMade = fyersFields.some(
    (key) => fyersData[key] !== originalData[key],
  );

  const allRequiredFilled = requiredFields.every(
    (field) => fyersData[field]?.trim() !== "",
  );

  /* ---------------- UI ---------------- */
  return (
    <>
      <Loader loading={loading || saving} />

      <Box
        sx={{
          p: { xs: 2, md: 3 },
          animation: `${fadeIn} 0.4s ease`,
          minHeight: "100vh",
          width: "100%",

          display: "flex",
          justifyContent: "center",

          background: `
      radial-gradient(circle at 20% 20%, rgba(25,118,210,0.12), transparent 40%),
      radial-gradient(circle at 80% 30%, rgba(0,200,83,0.08), transparent 40%),
      linear-gradient(135deg, #f5f7fa, #e4ecf7)
    `,
        }}
      >
        {/* INNER WRAPPER */}
        <Box
          sx={{
            width: "100%",
            maxWidth: "1400px", // 🔥 FIX WIDTH HERE
          }}
        >
          {/* HEADER */}
          <Typography
            sx={{
              fontSize: 20,
              fontWeight: 700,
              mb: 2,
              color: "#073057",
            }}
          >
            🔐 Fyers Auth Manager
          </Typography>

          {/* -------- FYERS DETAILS -------- */}
          <Accordion sx={glassCard}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography fontWeight={700}>⚡ Fyers Details</Typography>
            </AccordionSummary>

            <AccordionDetails>
              {fyersFields.map((key) => {
                const value = fyersData[key];
                const changed = originalData[key] !== value;
                const isRequired = requiredFields.includes(key);

                return (
                  <Box key={key} sx={{ mb: 2 }}>
                    <Typography fontSize={13} fontWeight={600}>
                      {key.replace(/_/g, " ")}
                      {isRequired && <span style={{ color: "red" }}> *</span>}
                    </Typography>

                    <TextField
                      value={value}
                      onChange={(e) => handleChange(key, e.target.value)}
                      size="small"
                      fullWidth
                      sx={{
                        ...glassInput,
                        mt: 0.5,
                        background: changed
                          ? "rgba(255, 249, 196, 0.7)"
                          : "rgba(255,255,255,0.6)",
                      }}
                    />
                  </Box>
                );
              })}

              <Button
                sx={{
                  ...glassButton,
                  color: "#1b5e20",
                  border: "1px solid rgba(76,175,80,0.4)",
                  background:
                    "linear-gradient(135deg, rgba(232,245,233,0.8), rgba(200,230,201,0.4))",
                }}
                disabled={!changesMade || saving || !allRequiredFilled}
                onClick={handleSaveChanges}
              >
                {saving ? "Saving..." : "Save Changes"}
              </Button>
            </AccordionDetails>
          </Accordion>

          {/* -------- TOKEN -------- */}
          <Accordion sx={{ ...glassCard, mt: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography fontWeight={700}>
                🔐 Generate Auth Code & Token
              </Typography>
            </AccordionSummary>

            <AccordionDetails>
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  flexDirection: { xs: "column", sm: "row" },
                  mb: 2,
                }}
              >
                <Button
                  sx={{
                    ...glassButton,
                    color: "#832f02",
                    border: "1px solid rgba(255,152,0,0.4)",
                    background:
                      "linear-gradient(135deg, rgba(254, 196, 103, 0.8), rgba(255,224,178,0.4))",
                  }}
                  disabled={!fyersData?.AUTH_CODE_GEN_URL}
                  onClick={() =>
                    window.open(fyersData?.AUTH_CODE_GEN_URL, "_blank")
                  }
                >
                  Generate Auth Code
                </Button>

                <TextField
                  placeholder="Paste auth code..."
                  value={authCode}
                  onChange={(e) => setAuthCode(e.target.value)}
                  size="small"
                  sx={{ ...glassInput, flex: 1 }}
                />

                <Button
                  sx={{
                    ...glassButton,
                    color: "#0d47a1",
                    border: "1px solid rgba(81, 168, 254, 0.4)",
                    background:
                      "linear-gradient(135deg, rgba(120, 191, 243, 0.8), rgba(187,222,251,0.4))",
                  }}
                  onClick={handleGenerateAccessToken}
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={20} /> : "Generate Token"}
                </Button>
              </Box>

              {message && (
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    background: token
                      ? "rgba(46,125,50,0.2)"
                      : "rgba(255,193,7,0.2)",
                    backdropFilter: "blur(10px)",
                  }}
                >
                  {message}
                </Box>
              )}

              {token && (
                <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
                  <TextField
                    value={token}
                    fullWidth
                    size="small"
                    InputProps={{ readOnly: true }}
                    sx={glassInput}
                  />

                  <IconButton
                    onClick={() => handleCopy(token)}
                    sx={glassButton}
                  >
                    <ContentCopyIcon />
                  </IconButton>
                </Box>
              )}
            </AccordionDetails>
          </Accordion>
        </Box>
      </Box>
    </>
  );
};

export default AuthCodeManager;
